import { PageIndices } from '../../common/i-doc/i-doc.registry.ts';
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  Skeleton,
  Card,
  CardContent,
  Typography,
} from '@mui/material';
import { DoneAll } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toastService } from '@/services/toast.service';

import { DashboardLayout } from '@/components/DashboardLayout';
import { UniversalPagination } from '@components/common/UniversalPagination';
import { expertPostsClientService } from '@/services/expert-posts-client.service';
import { ClientExpertPostDetail, ClientExpertPostListItem } from '@/types/expert-posts';
import { ExpertPostCard } from '@components/expert-posts/ExpertPostCard';
import { ExpertPostDetailModal } from '@components/expert-posts/ExpertPostDetailModal';
import { useAppDispatch } from '@/store';
import { decrementBadge, updateBadge } from '@/store/updates-hub/updatesHubSlice';
import { PageHint } from '@/components/common/PageHint';

const ClientExpertPostsPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [posts, setPosts] = useState<ClientExpertPostListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(9);
  const [selectedPost, setSelectedPost] = useState<ClientExpertPostDetail | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [markingAllRead, setMarkingAllRead] = useState(false);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const response = await expertPostsClientService.getPosts(page, limit);
      setPosts(response.data);
      setTotalPages(Math.ceil(response.meta.total / limit));
    } catch {
      toastService.error(t('expertPosts.client.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, [page, limit]);

  const handleOpenPost = async (item: ClientExpertPostListItem) => {
    setSelectedPost({ ...item, content: '' });
    setLoadingDetails(true);

    if (!item.isRead) {
      setPosts((prev) => prev.map((p) => (p.id === item.id ? { ...p, isRead: true } : p)));
      dispatch(decrementBadge('expertPosts'));
    }

    try {
      const full = await expertPostsClientService.getPost(item.id);
      setSelectedPost(full);
    } catch {
      // keep partial data
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleClosePost = () => {
    setSelectedPost(null);
    setLoadingDetails(false);
  };

  const handleMarkAllRead = async () => {
    setMarkingAllRead(true);
    try {
      await expertPostsClientService.markAllRead();
      setPosts((prev) => prev.map((p) => ({ ...p, isRead: true })));
      dispatch(updateBadge({ key: 'expertPosts', value: 0 }));
      toastService.success(t('expertPosts.client.markAllReadSuccess'));
    } catch {
      toastService.error(t('expertPosts.client.markAllReadError'));
    } finally {
      setMarkingAllRead(false);
    }
  };

  return (
    <DashboardLayout data-i-doc={PageIndices.CLIENT_EXPERT_POSTS}>
      <Container maxWidth="lg">
        <PageHint iDoc={PageIndices.CLIENT_EXPERT_POSTS} />
        <Box mb={4} display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
          <Button
            variant="outlined"
            startIcon={<DoneAll />}
            onClick={handleMarkAllRead}
            disabled={markingAllRead || loading}
          >
            {t('expertPosts.client.markAllRead')}
          </Button>
        </Box>

        {loading ? (
          <Grid container spacing={3}>
            {[...Array(6)].map((_, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Card>
                  <Skeleton variant="rectangular" height={200} />
                  <CardContent>
                    <Skeleton variant="text" height={30} width="80%" />
                    <Skeleton variant="text" height={20} width="40%" />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : posts.length > 0 ? (
          <>
            <Grid container spacing={3}>
              {posts.map((post) => (
                <Grid item xs={12} sm={6} md={4} key={post.id}>
                  <ExpertPostCard post={post} onClick={() => handleOpenPost(post)} />
                </Grid>
              ))}
            </Grid>

            <UniversalPagination
              page={page}
              count={totalPages}
              limit={limit}
              limitOptions={[9, 18, 36]}
              onPageChange={(v) => { setPage(v); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              onLimitChange={(v) => { setLimit(v); setPage(1); }}
              storageKey="pagination:expert-posts"
              sx={{ mt: 4 }}
            />
          </>
        ) : (
          <Box textAlign="center" py={10}>
            <Typography variant="h6" color="text.secondary">
              {t('expertPosts.client.empty')}
            </Typography>
          </Box>
        )}

        <ExpertPostDetailModal
          post={selectedPost}
          loading={loadingDetails}
          onClose={handleClosePost}
        />
      </Container>
    </DashboardLayout>
  );
};

export default ClientExpertPostsPage;
